import { useCallback, useEffect, useMemo, useState } from 'react';

import { useBusiness } from '../contexts/BusinessContext';

import type { InventoryMovementMode } from '../lib/inventoryMovementModeStorage';

import {

  fetchInventoryConsumedUnitsThisMonth,

  fetchInventorySalesThisMonth,

} from '../lib/inventoryMovementMetrics';

import { getStockItems, type StockItem } from '../lib/stockItems';



export interface InventorySnippetMetrics {

  salesThisMonth: number;

  consumedUnitsThisMonth: number;

  totalValue: number;

}



export interface InventoryLowStockItem {

  id: string;

  name: string;

  quantity: number;

  reorderPoint: number;

  unit: string;

  categoryName: string | null;

}



function isLowStock(item: StockItem): boolean {
  if (item.track_inventory === false) return false; // made-to-order: no stock levels
  const reorderPoint = item.reorder_point;
  if (reorderPoint == null || reorderPoint <= 0) return false;
  return item.quantity <= reorderPoint;
}



function buildStockMetrics(items: StockItem[]): Pick<InventorySnippetMetrics, 'totalValue'> {
  return {
    totalValue: items.reduce(
      (sum, item) =>
        sum + (item.track_inventory === false ? 0 : item.quantity * (item.acquisition_cost ?? 0)),
      0
    ),
  };
}



function buildLowStockList(items: StockItem[]): InventoryLowStockItem[] {

  return items

    .filter(isLowStock)

    .sort((a, b) => a.quantity - b.quantity || a.name.localeCompare(b.name))

    .map((item) => ({

      id: item.id,

      name: item.name,

      quantity: item.quantity,

      reorderPoint: item.reorder_point!,

      unit: item.unit,

      categoryName: item.category?.name ?? null,

    }));

}



/** Stock metrics + low-stock list for the control-centre inventory widget. */

export function useInventorySnippetData(_movementMode: InventoryMovementMode) {

  const { selectedBusiness } = useBusiness();

  const businessId = selectedBusiness?.id ?? '';



  const [items, setItems] = useState<StockItem[]>([]);

  const [salesThisMonth, setSalesThisMonth] = useState(0);

  const [consumedUnitsThisMonth, setConsumedUnitsThisMonth] = useState(0);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);



  const refresh = useCallback(async () => {

    if (!businessId) {

      setItems([]);

      setSalesThisMonth(0);

      setConsumedUnitsThisMonth(0);

      setLoading(false);

      return;

    }



    setLoading(true);

    setError(null);

    try {

      const [stockItems, salesTotal, consumedUnits] = await Promise.all([

        getStockItems(businessId),

        fetchInventorySalesThisMonth(businessId),

        fetchInventoryConsumedUnitsThisMonth(businessId),

      ]);

      setItems(stockItems);

      setSalesThisMonth(salesTotal);

      setConsumedUnitsThisMonth(consumedUnits);

    } catch (err) {

      console.error('Error loading inventory snippet:', err);

      setError(err instanceof Error ? err.message : 'Failed to load inventory');

      setItems([]);

      setSalesThisMonth(0);

      setConsumedUnitsThisMonth(0);

    } finally {

      setLoading(false);

    }

  }, [businessId]);



  useEffect(() => {

    void refresh();

  }, [refresh]);



  const metrics = useMemo(

    (): InventorySnippetMetrics => ({

      salesThisMonth,

      consumedUnitsThisMonth,

      ...buildStockMetrics(items),

    }),

    [items, salesThisMonth, consumedUnitsThisMonth]

  );



  const lowStockItems = useMemo(() => buildLowStockList(items), [items]);



  return {

    metrics,

    lowStockItems,

    loading,

    error,

    refresh,

  };

}

